"use client";

import { c as _c } from "react/compiler-runtime";
import { useVirtualizer } from "@tanstack/react-virtual";
import { RefreshCcw, Copy, Brain, GlobeIcon, AlertTriangle, FileText, Info, GitFork, Loader2, ExternalLinkIcon, PlusIcon, ScrollTextIcon, TrendingDown, Undo2, XIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useRouter } from "next/navigation";
import { useRef, useState, useEffect, useCallback, memo, useMemo } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Actions, Action } from "@/components/ai-elements/actions";
import { ConversationScrollButton, VirtualScrollContext } from "@/components/ai-elements/conversation";
import { Image } from "@/components/ai-elements/image";
import { Loader } from "@/components/ai-elements/loader";
import { Message, MessageContent } from "@/components/ai-elements/message";
import { PromptInput, PromptInputActionAddAttachments, PromptInputActionMenu, PromptInputActionMenuContent, PromptInputActionMenuTrigger, PromptInputAttachment, PromptInputAttachments, PromptInputBody, PromptInputButton, PromptInputSpeechButton, PromptInputSubmit, PromptInputTextarea, PromptInputToolbar, PromptInputTools } from "@/components/ai-elements/prompt-input";
import { Reasoning, ReasoningContent, ReasoningTrigger } from "@/components/ai-elements/reasoning";
import { Response } from "@/components/ai-elements/response";
import { Source, Sources, SourcesContent, SourcesTrigger } from "@/components/ai-elements/sources";
import { Tool, ToolContent, ToolHeader, ToolInput, ToolOutput } from "@/components/ai-elements/tool";
import { AspectRatioIcon } from "@/components/playground/aspect-ratio-icon";
import { CreateSkillDialog } from "@/components/playground/create-skill-dialog";
import { Button } from "@/components/ui/button";
import { ImageZoom } from "@/components/ui/image-zoom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useSkills, type Skill } from "@/hooks/useSkills";
import { getHeroSuggestionGroups, sampleSuggestions, type HeroSuggestionGroup } from "@/lib/hero-suggestions";
import { GPT_IMAGE_SIZES } from "@/lib/image-gen";
import { parseImagePartToDataUrl } from "@/lib/image-utils";
import { parsePlaygroundMessageMetadata, type PlaygroundMessageMetadata } from "@/lib/message-metadata";
import { cn } from "@/lib/utils";
import { useI18n } from "@llmgateway/i18n/react";
import { getMarketingUrl } from "@llmgateway/shared/branding";
import type { Locale, TranslationValues } from "@llmgateway/i18n";
import type { UIMessage, ChatRequestOptions, ChatStatus } from "ai";
type Translate = (key: string, values?: TranslationValues) => string;
function getCaretCoordinates(textarea: HTMLTextAreaElement, position: number): {
  top: number;
  left: number;
  height: number;
} {
  const div = document.createElement("div");
  const style = window.getComputedStyle(textarea);
  for (const prop of ["font-family", "font-size", "font-weight", "font-style", "letter-spacing", "line-height", "padding-top", "padding-right", "padding-bottom", "padding-left", "border-top-width", "border-right-width", "border-bottom-width", "border-left-width", "box-sizing", "word-spacing"]) {
    div.style.setProperty(prop, style.getPropertyValue(prop));
  }
  div.style.position = "absolute";
  div.style.top = "0";
  div.style.left = "0";
  div.style.visibility = "hidden";
  div.style.whiteSpace = "pre-wrap";
  div.style.wordBreak = "break-word";
  div.style.width = `${textarea.offsetWidth}px`;
  div.style.height = "auto";
  div.appendChild(document.createTextNode(textarea.value.slice(0, position)));
  const span = document.createElement("span");
  span.textContent = "​";
  div.appendChild(span);
  div.appendChild(document.createTextNode(textarea.value.slice(position)));
  document.body.appendChild(div);
  const result = {
    top: span.offsetTop,
    left: span.offsetLeft,
    height: span.offsetHeight
  };
  document.body.removeChild(div);
  return result;
}
interface ChatUIProps {
  messages: UIMessage[];
  supportsImages: boolean;
  supportsAudio: boolean;
  supportsDocuments: boolean;
  supportsImageGen: boolean;
  sendMessage: (message: UIMessage, options?: ChatRequestOptions) => Promise<void>;
  selectedModel: string;
  text: string;
  setText: (text: string) => void;
  status: ChatStatus;
  stop: () => void;
  regenerate: () => void;
  reasoningEffort: "" | "minimal" | "low" | "medium" | "high";
  setReasoningEffort: (value: "" | "minimal" | "low" | "medium" | "high") => void;
  supportsReasoning: boolean;
  imageAspectRatio: "auto" | "1:1" | "9:16" | "16:9" | "3:4" | "4:3" | "3:2" | "2:3" | "5:4" | "4:5" | "21:9" | "1:4" | "4:1" | "1:8" | "8:1";
  setImageAspectRatio: (value: "auto" | "1:1" | "9:16" | "16:9" | "3:4" | "4:3" | "3:2" | "2:3" | "5:4" | "4:5" | "21:9" | "1:4" | "4:1" | "1:8" | "8:1") => void;
  imageSize: string;
  setImageSize: (value: string) => void;
  alibabaImageSize: string;
  setAlibabaImageSize: (value: string) => void;
  imageQuality: string;
  setImageQuality: (value: string) => void;
  imageCount: 1 | 2 | 3 | 4;
  setImageCount: (value: 1 | 2 | 3 | 4) => void;
  supportsWebSearch: boolean;
  webSearchEnabled: boolean;
  setWebSearchEnabled: (value: boolean) => void;
  onUserMessage?: (content: string, images?: Array<{
    type: "image_url";
    image_url: {
      url: string;
    };
  }>, audio?: Array<{
    type: "audio";
    url: string;
    mediaType: string;
    name?: string;
  }>, documents?: Array<{
    type: "file";
    url: string;
    mediaType: string;
    name?: string;
  }>) => Promise<{
    id: string;
  } | undefined>;
  onEditUserMessage?: (message: UIMessage, content: string) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  finishReason?: string | null;
  floatingInput?: boolean;
  isTemporaryChat?: boolean;
  forkChat?: () => void | Promise<void>;
  isForkingChat?: boolean;
  activeSkills?: Skill[];
  onSelectSkill?: (skill: Skill) => void;
  onRemoveSkill?: (skillId: string) => void;
}
function getRandomHeroSuggestionGroups(locale: Locale): Record<HeroSuggestionGroup, readonly string[]> {
  const suggestions = getHeroSuggestionGroups(locale);
  return {
    Create: sampleSuggestions(suggestions.Create, 5),
    Explore: sampleSuggestions(suggestions.Explore, 5),
    Code: sampleSuggestions(suggestions.Code, 5),
    "Image gen": sampleSuggestions(suggestions["Image gen"], 5)
  };
}

// js-combine-iterations: Extract message parts in a single pass instead of multiple filter() calls
interface ExtractedParts {
  textParts: string[];
  imageParts: any[];
  audioParts: any[];
  documentParts: any[];
  toolParts: any[];
  reasoningContent: string;
  sourceParts: any[];
}
function isDocumentMediaType(mediaType: string | null | undefined): boolean {
  // A "document" is anything that isn't an image or audio. We forward the
  // MIME to the gateway verbatim and let the provider reject it if it's not
  // supported — `UnsupportedDocumentFormatError` surfaces those rejections
  // as a clean 400 with the actual MIME the provider refused.
  if (!mediaType) {
    return true;
  }
  if (mediaType.startsWith("image/") || mediaType.startsWith("audio/")) {
    return false;
  }
  return true;
}
function getDocumentMediaType(mediaType: string | null | undefined): string {
  return mediaType && isDocumentMediaType(mediaType) ? mediaType : "application/octet-stream";
}
function extractMessageParts(parts: any[]): ExtractedParts {
  const textParts: string[] = [];
  const imageParts: any[] = [];
  const audioParts: any[] = [];
  const documentParts: any[] = [];
  const toolParts: any[] = [];
  const reasoningParts: string[] = [];
  const sourceParts: any[] = [];
  for (const p of parts) {
    if (p.type === "text") {
      textParts.push(p.text);
    } else if (p.type === "reasoning") {
      reasoningParts.push(p.text);
    } else if (p.type.startsWith("tool-")) {
      // AI SDK v6 uses tool-{toolName} as the part type (e.g., "tool-fetch_weather")
      toolParts.push(p);
    } else if (p.type === "source-url") {
      sourceParts.push(p);
    } else if (p.type === "image_url" && p.image_url?.url || p.type === "file" && p.mediaType?.startsWith("image/")) {
      imageParts.push(p);
    } else if (p.type === "file" && p.mediaType?.startsWith("audio/")) {
      audioParts.push(p);
    } else if (p.type === "file" && isDocumentMediaType(p.mediaType)) {
      documentParts.push(p);
    }
  }
  return {
    textParts,
    imageParts,
    audioParts,
    documentParts,
    toolParts,
    reasoningContent: reasoningParts.join(""),
    sourceParts
  };
}
function getFinishReasonLabel(reason: string, t: Translate): string {
  switch (reason) {
    case "length":
      return t("chatUi.responseReachedMaxTokens");
    case "content-filter":
    case "content_filter":
      return t("chatUi.responseContentFiltered");
    default:
      return t("chatUi.generationStopped", {
        reason
      });
  }
}
function formatTokenCount(value?: number): string {
  return value === undefined ? "-" : new Intl.NumberFormat("en-US").format(value);
}
function formatCost(value?: number): string {
  if (value === undefined) {
    return "-";
  }
  if (value > 0 && value < 0.000001) {
    return "<$0.000001";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: value < 0.01 ? 6 : 2,
    maximumFractionDigits: value < 0.01 ? 6 : 4
  }).format(value);
}
function getMessageImageGridClass(imageCount: number, alignEnd = false) {
  return cn("mt-3 gap-3", imageCount === 1 ? "grid grid-cols-1" : cn("flex max-w-full flex-row flex-wrap", alignEnd ? "justify-end" : "justify-start"), alignEnd && imageCount === 1 && "justify-items-end");
}
function getUserMessageWidthClass(imageCount: number, isEditing?: boolean) {
  if (isEditing) {
    return "w-full max-w-full";
  }
  return imageCount > 1 ? "w-fit max-w-[80%] min-w-64" : "w-fit max-w-[80%]";
}
function getMessageImageClass(imageCount: number, singleImageClassName: string) {
  return cn("border rounded-lg object-cover", imageCount === 1 ? singleImageClassName : "size-24 aspect-square sm:size-28");
}
function MessageMetadataPopover(t0) {
  const $ = _c(76);
  const {
    metadata
  } = t0;
  const {
    t
  } = useI18n();
  const [open, setOpen] = useState(false);
  const discount = metadata.discount;
  const logId = metadata.logId;
  const organizationId = metadata.organizationId;
  const projectId = metadata.projectId;
  const usage = metadata.usage;
  let t1;
  if ($[0] !== t) {
    t1 = t("chatUi.totalCost");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = usage?.totalCost;
  let t3;
  if ($[2] !== t2) {
    t3 = formatCost(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t3) {
    t4 = [t1, t3];
    $[4] = t1;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("chatUi.inputTokens");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = usage?.inputTokens;
  let t7;
  if ($[9] !== t6) {
    t7 = formatTokenCount(t6);
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t7) {
    t8 = [t5, t7];
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t) {
    t9 = t("chatUi.cachedInputTokens");
    $[14] = t;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = usage?.cachedInputTokens;
  let t11;
  if ($[16] !== t10) {
    t11 = formatTokenCount(t10);
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t9) {
    t12 = [t9, t11];
    $[18] = t11;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t) {
    t13 = t("chatUi.outputTokens");
    $[21] = t;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const t14 = usage?.outputTokens;
  let t15;
  if ($[23] !== t14) {
    t15 = formatTokenCount(t14);
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t13 || $[26] !== t15) {
    t16 = [t13, t15];
    $[25] = t13;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t) {
    t17 = t("chatUi.usedModel");
    $[28] = t;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  const t18 = metadata.usedModel ?? "-";
  let t19;
  if ($[30] !== t17 || $[31] !== t18) {
    t19 = [t17, t18];
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t12 || $[34] !== t16 || $[35] !== t19 || $[36] !== t4 || $[37] !== t8) {
    t20 = [t4, t8, t12, t16, t19];
    $[33] = t12;
    $[34] = t16;
    $[35] = t19;
    $[36] = t4;
    $[37] = t8;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  const rows = t20 as const;
  let t21;
  if ($[39] !== t) {
    t21 = t("chatUi.responseMetadata");
    $[39] = t;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Info className="size-3" />;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t21) {
    t23 = <TooltipTrigger asChild={true}><PopoverTrigger asChild={true}><Button aria-label={t21} className="relative size-9 p-1.5 text-muted-foreground hover:text-foreground" size="sm" type="button" variant="ghost">{t22}</Button></PopoverTrigger></TooltipTrigger>;
    $[42] = t21;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== t) {
    t24 = t("chatUi.responseMetadata");
    $[44] = t;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t24) {
    t25 = <TooltipContent><p>{t24}</p></TooltipContent>;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== t23 || $[49] !== t25) {
    t26 = <Tooltip>{t23}{t25}</Tooltip>;
    $[48] = t23;
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== t) {
    t27 = t("chatUi.responseMetadata");
    $[51] = t;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== t27) {
    t28 = <p className="font-medium">{t27}</p>;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== rows) {
    t29 = rows.map(_temp);
    $[55] = rows;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== discount || $[58] !== t) {
    t30 = discount !== null && discount !== undefined && discount > 0 && <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-3"><span className="text-muted-foreground">{t("chatUi.discount")}</span><span className="flex items-center justify-end gap-1 font-mono text-emerald-500"><TrendingDown className="h-3 w-3" />{(discount * 100).toFixed(0)}% off</span></div>;
    $[57] = discount;
    $[58] = t;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== logId || $[61] !== organizationId || $[62] !== projectId || $[63] !== t) {
    t31 = logId && organizationId && projectId && <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-3"><span className="text-muted-foreground">{t("chatUi.activityLog")}</span><span className="flex items-center justify-end"><a href={`${process.env.NODE_ENV === "development" ? "http://localhost:3002" : getMarketingUrl()}/dashboard/${organizationId}/${projectId}/activity/${logId}`} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground"><ExternalLinkIcon className="h-3 w-3" /></a></span></div>;
    $[60] = logId;
    $[61] = organizationId;
    $[62] = projectId;
    $[63] = t;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== t29 || $[66] !== t30 || $[67] !== t31) {
    t32 = <div className="space-y-1.5">{t29}{t30}{t31}</div>;
    $[65] = t29;
    $[66] = t30;
    $[67] = t31;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== t28 || $[70] !== t32) {
    t33 = <PopoverContent align="start" className="w-80 p-3"><div className="space-y-2 text-xs">{t28}{t32}</div></PopoverContent>;
    $[69] = t28;
    $[70] = t32;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== open || $[73] !== t26 || $[74] !== t33) {
    t34 = <Popover open={open} onOpenChange={setOpen}>{t26}{t33}</Popover>;
    $[72] = open;
    $[73] = t26;
    $[74] = t33;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  return t34;
}

// rerender-memo: Memoize message component to prevent re-renders when only streaming status changes
function _temp(t0) {
  const [label, value] = t0;
  return <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-3" key={label}><span className="text-muted-foreground">{label}</span><span className="break-words text-right font-mono">{value}</span></div>;
}
const AssistantMessage = memo(t0 => {
  const $ = _c(46);
  const {
    message,
    isLastMessage,
    status,
    regenerate,
    finishReason,
    forkChat,
    isForkingChat
  } = t0;
  const {
    t
  } = useI18n();
  let t1;
  if ($[0] !== message.parts) {
    t1 = extractMessageParts(message.parts);
    $[0] = message.parts;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    textParts,
    imageParts,
    toolParts,
    reasoningContent,
    sourceParts
  } = t1;
  let t2;
  if ($[2] !== message.metadata) {
    t2 = parsePlaygroundMessageMetadata(message.metadata);
    $[2] = message.metadata;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const metadata = t2;
  let t3;
  if ($[4] !== textParts) {
    t3 = textParts.join("");
    $[4] = textParts;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const textContent = t3;
  let t4;
  if ($[6] !== isLastMessage || $[7] !== reasoningContent || $[8] !== status || $[9] !== textContent) {
    t4 = reasoningContent ? <Reasoning className="w-full" defaultOpen={status === "streaming" && isLastMessage && textContent === ""} isStreaming={status === "streaming" && isLastMessage && textContent === ""}><ReasoningTrigger /><ReasoningContent>{reasoningContent}</ReasoningContent></Reasoning> : null;
    $[6] = isLastMessage;
    $[7] = reasoningContent;
    $[8] = status;
    $[9] = textContent;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== toolParts) {
    t5 = toolParts.map(_temp2);
    $[11] = toolParts;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== isLastMessage || $[14] !== status || $[15] !== textContent) {
    t6 = textContent ? <Response isStreaming={status === "streaming" && isLastMessage}>{textContent}</Response> : null;
    $[13] = isLastMessage;
    $[14] = status;
    $[15] = textContent;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== imageParts || $[18] !== isLastMessage || $[19] !== status || $[20] !== t) {
    t7 = imageParts.length > 0 ? <div className={getMessageImageGridClass(imageParts.length)}>{imageParts.map((part, idx) => {
        const {
          base64Only,
          mediaType
        } = parseImagePartToDataUrl(part);
        if (!base64Only) {
          return null;
        }
        return <ImageZoom key={idx}><Image base64={base64Only} mediaType={mediaType} alt={part.name ?? t("chatUi.generatedImage")} className={getMessageImageClass(imageParts.length, "h-[400px] aspect-auto")} /></ImageZoom>;
      })}</div> : isLastMessage && status === "streaming" ? <div className="mt-3"><Loader /></div> : null;
    $[17] = imageParts;
    $[18] = isLastMessage;
    $[19] = status;
    $[20] = t;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== message.id || $[23] !== sourceParts) {
    t8 = sourceParts.length > 0 ? <Sources><SourcesTrigger count={sourceParts.length} /><SourcesContent>{sourceParts.map((part_0, i) => <Source key={`${message.id}-source-${i}`} href={part_0.url} title={part_0.title ?? part_0.url} />)}</SourcesContent></Sources> : null;
    $[22] = message.id;
    $[23] = sourceParts;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== finishReason || $[26] !== isLastMessage || $[27] !== t) {
    t9 = isLastMessage && finishReason && <div className="mt-2 flex items-center gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200"><AlertTriangle className="size-3.5 shrink-0" /><span>{getFinishReasonLabel(finishReason, t)}</span></div>;
    $[25] = finishReason;
    $[26] = isLastMessage;
    $[27] = t;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== forkChat || $[30] !== isForkingChat || $[31] !== isLastMessage || $[32] !== metadata || $[33] !== regenerate || $[34] !== status || $[35] !== t || $[36] !== textContent) {
    t10 = (metadata || isLastMessage && status !== "streaming") && <Actions className="mt-2">{metadata ? <MessageMetadataPopover metadata={metadata} /> : null}{isLastMessage && status !== "streaming" ? <><Action onClick={() => regenerate()} label={t("chatUi.retry")} tooltip={t("chatUi.regenerateResponse")}><RefreshCcw className="size-3" /></Action>{forkChat ? <Action disabled={isForkingChat} onClick={() => {
          forkChat();
        }} label={t("chatUi.forkChat")} tooltip={t("chatUi.forkChat")}>{isForkingChat ? <Loader2 className="size-3 animate-spin" /> : <GitFork className="size-3" />}</Action> : null}<Action onClick={async () => {
          try {
            await navigator.clipboard.writeText(textContent);
            toast.success(t("chatUi.copiedToClipboard"));
          } catch {
            toast.error(t("chatUi.failedToCopyToClipboard"));
          }
        }} label={t("chatUi.copy")} tooltip={t("chatUi.copyToClipboard")}><Copy className="size-3" /></Action></> : null}</Actions>;
    $[29] = forkChat;
    $[30] = isForkingChat;
    $[31] = isLastMessage;
    $[32] = metadata;
    $[33] = regenerate;
    $[34] = status;
    $[35] = t;
    $[36] = textContent;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== t10 || $[39] !== t4 || $[40] !== t5 || $[41] !== t6 || $[42] !== t7 || $[43] !== t8 || $[44] !== t9) {
    t11 = <div className="message-item">{t4}{t5}{t6}{t7}{t8}{t9}{t10}</div>;
    $[38] = t10;
    $[39] = t4;
    $[40] = t5;
    $[41] = t6;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  return t11;
});

// rerender-memo: Memoize user message component
const UserMessage = memo(({
  message,
  isLastMessage: _isLastMessage,
  status: _status,
  canEdit,
  isEditing,
  onEditStart,
  onEditCancel,
  onEditConfirm
}: {
  message: UIMessage;
  isLastMessage: boolean;
  status: string;
  canEdit?: boolean;
  isEditing?: boolean;
  onEditStart?: () => void;
  onEditCancel?: () => void;
  onEditConfirm?: (content: string) => Promise<void>;
}) => {
  const {
    t
  } = useI18n();
  const {
    textParts,
    imageParts,
    audioParts,
    documentParts
  } = useMemo(() => extractMessageParts(message.parts), [message.parts]);
  const initialText = textParts.join("\n");
  const [editText, setEditText] = useState(initialText);
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    if (isEditing) {
      setEditText(initialText);
    }
  }, [initialText, isEditing]);
  const handleEditConfirm = async () => {
    if (!onEditConfirm || isSaving) {
      return;
    }
    if (!editText.trim() && imageParts.length === 0) {
      return;
    }
    setIsSaving(true);
    try {
      await onEditConfirm(editText);
    } finally {
      setIsSaving(false);
    }
  };
  return <Message from={message.role} className="message-item group/user-message">
				<div className={cn("flex flex-col items-end", getUserMessageWidthClass(imageParts.length, isEditing))}>
					<MessageContent className={cn("!max-w-full", isEditing && "w-full px-5 py-4")} variant="flat">
						{isEditing ? <div className="flex w-full min-w-0 flex-col gap-3">
								<Textarea value={editText} onChange={event => setEditText(event.currentTarget.value)} className="min-h-24 w-full min-w-0 resize-y bg-background text-foreground" autoFocus />
								<div className="flex flex-wrap justify-end gap-2">
									<Button type="button" size="sm" variant="secondary" onClick={onEditCancel} disabled={isSaving} className="rounded-full">
										{t("common.cancel")}
									</Button>
									<Button type="button" size="sm" onClick={() => void handleEditConfirm()} disabled={isSaving || !editText.trim() && imageParts.length === 0} className="rounded-full">
										{t("chatUi.send")}
									</Button>
								</div>
							</div> : <>
								{textParts.map((t_0, idx) => <div key={idx}>{t_0}</div>)}
							</>}
						{imageParts.length > 0 && <div className={getMessageImageGridClass(imageParts.length, true)}>
								{imageParts.map((part: any, idx_0: number) => {
            const {
              base64Only,
              mediaType
            } = parseImagePartToDataUrl(part);
            if (!base64Only) {
              return null;
            }
            return <ImageZoom key={idx_0}>
											<Image base64={base64Only} mediaType={mediaType} alt={part.name ?? t("chatUi.uploadedImage")} className={getMessageImageClass(imageParts.length, "h-[300px] aspect-auto")} />
										</ImageZoom>;
          })}
							</div>}
						{audioParts.length > 0 && <div className="mt-3 flex flex-col gap-2">
								{audioParts.map((part_0: any, idx_1: number) => <audio key={idx_1} controls src={part_0.url} className="w-full max-w-md" aria-label={part_0.name ?? part_0.filename ?? t("chatUi.audioAttachment")}>
										<track kind="captions" />
									</audio>)}
							</div>}
						{documentParts.length > 0 && <div className="mt-3 flex flex-wrap gap-2">
								{documentParts.map((part_1: any, idx_2: number) => {
            const name = part_1.name ?? part_1.filename ?? t("chatUi.document");
            const mediaType_0: string = part_1.mediaType ?? "";
            return <a key={idx_2} href={part_1.url} download={name} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm hover:bg-muted transition-colors max-w-xs" title={`${name} (${mediaType_0 || "document"})`}>
											<FileText className="size-4 shrink-0 opacity-70" />
											<span className="truncate">{name}</span>
										</a>;
          })}
							</div>}
					</MessageContent>
					{!isEditing ? <Actions className="mt-2 opacity-0 transition-opacity group-hover/user-message:opacity-100 focus-within:opacity-100">
							{canEdit ? <Action onClick={onEditStart} label={t("chatUi.editAndRetry")} tooltip={t("chatUi.editAndRetryFromHere")}>
									<Undo2 className="size-3" />
								</Action> : null}
							<Action onClick={async () => {
          try {
            await navigator.clipboard.writeText(initialText);
            toast.success(t("chatUi.copiedToClipboard"));
          } catch {
            toast.error(t("chatUi.failedToCopyToClipboard"));
          }
        }} label={t("chatUi.copy")} tooltip={t("chatUi.copyToClipboard")}>
								<Copy className="size-3" />
							</Action>
						</Actions> : null}
				</div>
			</Message>;
});
const VirtualUserMessageItem = memo(t0 => {
  const $ = _c(18);
  const {
    message,
    isLastMessage,
    status,
    canEdit,
    editingMessageId,
    setEditingMessageId,
    onEditUserMessage
  } = t0;
  let t1;
  if ($[0] !== message.id || $[1] !== setEditingMessageId) {
    t1 = () => setEditingMessageId(message.id);
    $[0] = message.id;
    $[1] = setEditingMessageId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleEditStart = t1;
  let t2;
  if ($[3] !== setEditingMessageId) {
    t2 = () => setEditingMessageId(null);
    $[3] = setEditingMessageId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleEditCancel = t2;
  let t3;
  if ($[5] !== message || $[6] !== onEditUserMessage || $[7] !== setEditingMessageId) {
    t3 = async content => {
      if (!onEditUserMessage) {
        return;
      }
      try {
        await onEditUserMessage(message, content);
        setEditingMessageId(null);
      } catch {}
    };
    $[5] = message;
    $[6] = onEditUserMessage;
    $[7] = setEditingMessageId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleEditConfirm = t3;
  const t4 = editingMessageId === message.id;
  let t5;
  if ($[9] !== canEdit || $[10] !== handleEditCancel || $[11] !== handleEditConfirm || $[12] !== handleEditStart || $[13] !== isLastMessage || $[14] !== message || $[15] !== status || $[16] !== t4) {
    t5 = <UserMessage message={message} isLastMessage={isLastMessage} status={status} canEdit={canEdit} isEditing={t4} onEditStart={handleEditStart} onEditCancel={handleEditCancel} onEditConfirm={handleEditConfirm} />;
    $[9] = canEdit;
    $[10] = handleEditCancel;
    $[11] = handleEditConfirm;
    $[12] = handleEditStart;
    $[13] = isLastMessage;
    $[14] = message;
    $[15] = status;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
});
VirtualUserMessageItem.displayName = "VirtualUserMessageItem";
const MESSAGE_ESTIMATE_SIZE = 74;
const LOADER_HEIGHT = 52;
const ERROR_BANNER_HEIGHT = 44;
export function ReadOnlyChatMessages({
  messages
}: {
  messages: UIMessage[];
}) {
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => MESSAGE_ESTIMATE_SIZE,
    getItemKey: index => messages[index]!.id,
    anchorTo: "end",
    followOnAppend: false,
    scrollEndThreshold: 80,
    overscan: 6
  });
  return <div ref={parentRef} className="flex-1 overflow-y-auto" role="log">
			<div className="mx-auto max-w-4xl relative" style={{
      height: virtualizer.getTotalSize()
    }}>
				{virtualizer.getVirtualItems().map(item => {
        const message = messages[item.index]!;
        return <div key={item.key} data-index={item.index} ref={virtualizer.measureElement} className="px-4 py-2" style={{
          position: "absolute",
          top: 0,
          left: 0,
          transform: `translateY(${item.start}px)`,
          width: "100%"
        }}>
							{message.role === "assistant" ? <AssistantMessage message={message} isLastMessage={false} status="ready" regenerate={() => {}} finishReason={null} /> : <UserMessage message={message} isLastMessage={false} status="ready" />}
						</div>;
      })}
			</div>
		</div>;
}
export const ChatUI = ({
  messages,
  supportsImages,
  supportsAudio,
  supportsDocuments,
  supportsImageGen,
  sendMessage,
  selectedModel,
  text,
  setText,
  status,
  stop,
  regenerate,
  reasoningEffort,
  setReasoningEffort,
  supportsReasoning,
  imageAspectRatio,
  setImageAspectRatio,
  imageSize,
  setImageSize,
  alibabaImageSize,
  setAlibabaImageSize,
  imageQuality,
  setImageQuality,
  imageCount,
  setImageCount,
  supportsWebSearch,
  webSearchEnabled,
  setWebSearchEnabled,
  onUserMessage,
  onEditUserMessage,
  isLoading = false,
  error = null,
  finishReason = null,
  floatingInput = false,
  isTemporaryChat = false,
  forkChat,
  isForkingChat = false,
  activeSkills = [],
  onSelectSkill,
  onRemoveSkill
}: ChatUIProps) => {
  const {
    locale,
    t
  } = useI18n();
  // OpenAI gpt-image-2 uses pixel dimensions and supports a quality dropdown
  const isGptImage = selectedModel.toLowerCase().includes("gpt-image") || selectedModel.toLowerCase().includes("openai/gpt-image");

  // Check if the model uses WIDTHxHEIGHT format (Alibaba, ZAI, or OpenAI gpt-image)
  const usesPixelDimensions = isGptImage || selectedModel.toLowerCase().includes("alibaba") || selectedModel.toLowerCase().includes("qwen-image") || selectedModel.toLowerCase().includes("zai") || selectedModel.toLowerCase().includes("cogview");

  // Seedream/ByteDance models only support 2K and 4K
  const isSeedream = selectedModel.toLowerCase().includes("seedream") || selectedModel.toLowerCase().includes("bytedance/seedream");

  // Gemini 3.1 Flash Image supports 0.5K, 1K (default), 2K, 4K
  const isGemini31FlashImage = selectedModel.toLowerCase().includes("gemini-3.1-flash-image");
  const availableSizes = isSeedream ? ["2K", "4K"] as const : isGemini31FlashImage ? ["0.5K", "1K", "2K", "4K"] as const : ["1K", "2K", "4K"] as const;
  const qualityOptions = ["auto", "low", "medium", "high"] as const;
  const getSuggestionGroupLabel = (group: HeroSuggestionGroup) => {
    switch (group) {
      case "Create":
        return t("chatUi.suggestionCreate");
      case "Explore":
        return t("chatUi.suggestionExplore");
      case "Code":
        return t("chatUi.suggestionCode");
      case "Image gen":
        return t("chatUi.suggestionImageGen");
      default:
        return group;
    }
  };
  const getReasoningLabel = (value: "off" | "minimal" | "low" | "medium" | "high") => {
    switch (value) {
      case "off":
        return t("chatUi.auto");
      case "minimal":
        return t("chatUi.minimal");
      case "low":
        return t("chatUi.reasoningLow");
      case "medium":
        return t("chatUi.reasoningMedium");
      case "high":
        return t("chatUi.reasoningHigh");
    }
  };
  const [activeGroup, setActiveGroup] = useState<HeroSuggestionGroup>("Create");
  const [randomizedHeroSuggestionGroups, setRandomizedHeroSuggestionGroups] = useState<Record<HeroSuggestionGroup, readonly string[]> | null>(null);
  useEffect(() => setRandomizedHeroSuggestionGroups(getRandomHeroSuggestionGroups(locale)), [locale]);
  const visibleHeroSuggestionGroups: HeroSuggestionGroup[] = supportsImageGen ? ["Image gen"] : ["Create", "Explore", "Code"];
  const activeSuggestionGroup: HeroSuggestionGroup = supportsImageGen ? "Image gen" : visibleHeroSuggestionGroups.includes(activeGroup) ? activeGroup : "Create";
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const inputRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [inputHeight, setInputHeight] = useState(0);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [isAtEnd, setIsAtEnd] = useState(true);
  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => MESSAGE_ESTIMATE_SIZE,
    getItemKey: index => messages[index]!.id,
    anchorTo: "end",
    followOnAppend: true,
    scrollEndThreshold: 80,
    overscan: 6
  });
  const virtualizerRef = useRef(virtualizer);
  virtualizerRef.current = virtualizer;
  const wasAtEndRef = useRef(true);
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    const virtNext = virtualizerRef.current.isAtEnd(80);
    const domNext = el ? el.scrollHeight - el.scrollTop - el.clientHeight < 80 : virtNext;
    wasAtEndRef.current = domNext;
    setIsAtEnd(prev => prev === domNext ? prev : domNext);
  }, []);
  const scrollToEnd = useCallback(() => {
    virtualizerRef.current.scrollToEnd();
  }, []);
  const [skillTriggerOpen, setSkillTriggerOpen] = useState(false);
  const [skillTriggerPos, setSkillTriggerPos] = useState({
    top: 0,
    left: 0
  });
  const [skillTriggerFilter, setSkillTriggerFilter] = useState("");
  const skillTriggerIndexRef = useRef(-1);
  const handleSkillTriggerSelect = useCallback((skill: Skill) => {
    const before = text.slice(0, skillTriggerIndexRef.current);
    const after = text.slice(skillTriggerIndexRef.current + 1 + skillTriggerFilter.length);
    setText(before + after);
    onSelectSkill?.(skill);
    setSkillTriggerOpen(false);
    skillTriggerIndexRef.current = -1;
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [text, skillTriggerFilter, setText, onSelectSkill]);
  const updateInputHeight = useCallback(() => {
    if (inputRef.current) {
      setInputHeight(inputRef.current.offsetHeight);
    }
  }, []);
  useEffect(() => {
    updateInputHeight();
    const observer = new ResizeObserver(updateInputHeight);
    if (inputRef.current) {
      observer.observe(inputRef.current);
    }
    return () => observer.disconnect();
  }, [updateInputHeight]);
  useEffect(() => {
    if (!floatingInput) {
      return;
    }
    const handleSelectionChange = () => {
      const input = inputRef.current;
      if (!input) {
        return;
      }
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        return;
      }
      const range = selection.getRangeAt(0);
      if (!range.intersectsNode(input)) {
        return;
      }
      if (input.contains(range.startContainer)) {
        return;
      }
      try {
        const trimmed = range.cloneRange();
        trimmed.setEndBefore(input);
        selection.removeAllRanges();
        if (!trimmed.collapsed) {
          selection.addRange(trimmed);
        }
      } catch {
        selection.removeAllRanges();
      }
    };
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, [floatingInput]);
  // Centralized busy/active gates: isBusy blocks new submissions; isActive
  // governs the Stop button which should only show while a request is in flight.
  const isActive = status === "streaming" || status === "submitted";
  const isBusy = isLoading || isActive;
  const canEditUserMessages = !isBusy && !isTemporaryChat && !!onEditUserMessage;
  const handlePromptSubmit = async (textContent: string, files?: Array<{
    url?: string | null;
    mediaType?: string | null;
    filename?: string | null;
  }>) => {
    if (isActive) {
      stop();
      return;
    }
    if (isBusy) {
      return;
    }
    try {
      const content = textContent ?? "";
      if (!content.trim() && !files?.length) {
        return;
      }
      setText(""); // Clear input immediately

      const parts: any[] = [];
      const imagesToSave = supportsImages && files?.length ? files.filter(f => f.mediaType?.startsWith("image/") && f.url).map(f_0 => ({
        type: "image_url" as const,
        image_url: {
          url: f_0.url!
        }
      })) : undefined;
      const audioToSave = supportsAudio && files?.length ? files.filter(f_1 => f_1.mediaType?.startsWith("audio/") && f_1.url).map(f_2 => ({
        type: "audio" as const,
        url: f_2.url!,
        mediaType: f_2.mediaType!,
        ...(f_2.filename ? {
          name: f_2.filename
        } : {})
      })) : undefined;
      const documentsToSave = supportsDocuments && files?.length ? files.filter(f_3 => f_3.url && isDocumentMediaType(f_3.mediaType)).map(f_4 => ({
        type: "file" as const,
        url: f_4.url!,
        mediaType: getDocumentMediaType(f_4.mediaType),
        ...(f_4.filename ? {
          name: f_4.filename
        } : {})
      })) : undefined;
      if (content.trim()) {
        parts.push({
          type: "text",
          text: content
        });
      }

      // Attach user images/files as AI SDK "file" parts so vision /
      // image-generation models can actually consume them.
      if (supportsImages && files?.length) {
        for (const file of files) {
          if (file.mediaType?.startsWith("image/") && file.url) {
            parts.push({
              type: "file",
              url: file.url,
              mediaType: file.mediaType,
              name: file.filename
            });
          }
        }
      }
      if (supportsAudio && files?.length) {
        for (const file_0 of files) {
          if (file_0.mediaType?.startsWith("audio/") && file_0.url) {
            parts.push({
              type: "file",
              url: file_0.url,
              mediaType: file_0.mediaType,
              name: file_0.filename
            });
          }
        }
      }
      if (supportsDocuments && files?.length) {
        for (const file_1 of files) {
          if (file_1.url && isDocumentMediaType(file_1.mediaType)) {
            parts.push({
              type: "file",
              url: file_1.url,
              mediaType: getDocumentMediaType(file_1.mediaType),
              name: file_1.filename
            });
          }
        }
      }
      if (parts.length === 0) {
        return;
      }
      const generatedMessageId = crypto.randomUUID();
      let savedMessage: {
        id: string;
      } | undefined;

      // Ensure the chat exists + user message is persisted BEFORE streaming starts.
      // Otherwise `onFinish` may run before `chatIdRef` is set, and we can't save the AI response.
      if (onUserMessage && (content.trim() || imagesToSave?.length || audioToSave?.length || documentsToSave?.length)) {
        savedMessage = (await onUserMessage(content, imagesToSave, audioToSave, documentsToSave)) ?? undefined;
      }

      // If a persistent chat was expected (onUserMessage provided) but persistence
      // returned nothing, a stop condition was hit (credits, limit, etc.).
      // Temporary chats intentionally return undefined — streaming must still proceed.
      if (onUserMessage && !savedMessage && !isTemporaryChat) {
        return;
      }

      // Call sendMessage which will handle adding the user message and API request
      await sendMessage({
        id: savedMessage?.id ?? generatedMessageId,
        role: "user",
        parts
      }, {
        body: {
          model: selectedModel
        }
      });
    } catch (e) {
      toast.error(`Could not send message. ${e instanceof Error ? e.message : ""}`.trim());
    }
  };
  const virtualItems = virtualizer.getVirtualItems();
  const showSubmittedLoader = status === "submitted";
  const showErrorBanner = messages.length > 0 && messages[messages.length - 1]!.role === "user" && !!error;
  const extraScrollHeight = showSubmittedLoader ? LOADER_HEIGHT : showErrorBanner ? ERROR_BANNER_HEIGHT : 0;
  const totalSize = virtualizer.getTotalSize();
  useEffect(() => {
    if (messages.length > 0 && wasAtEndRef.current) {
      requestAnimationFrame(() => virtualizerRef.current.scrollToEnd());
    }
  }, [totalSize, messages.length, inputHeight]);
  useEffect(() => {
    if (status === "submitted") {
      requestAnimationFrame(() => virtualizerRef.current.scrollToEnd());
    }
    if (status === "ready" && virtualizerRef.current.isAtEnd(80)) {
      virtualizerRef.current.scrollToEnd();
    }
  }, [status]);
  const messagesContent = isLoading && messages.length === 0 ? <div className="flex items-center justify-center h-full">
				<Loader />
			</div> : messages.length === 0 ? <AnimatePresence mode="wait" initial={false}>
				<motion.div key={isTemporaryChat ? "temporary-chat-empty" : "regular-chat-empty"} initial={{
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.96
    }} transition={{
      opacity: {
        duration: 0.06,
        ease: "easeOut"
      },
      scale: {
        duration: 0.14,
        ease: "easeOut"
      }
    }} className={`mx-auto w-full max-w-3xl ${isTemporaryChat ? "flex flex-1 items-center justify-center py-10" : "py-10"}`}>
					<motion.div className={`${isTemporaryChat ? "mb-0" : "mb-6"} text-center`} layout transition={{
        duration: 0.14,
        ease: "easeOut"
      }}>
						<h2 className="text-3xl font-semibold tracking-tight">
							{isTemporaryChat ? t("playground.temporaryChat") : t("chatUi.howCanIHelp")}
						</h2>
						<AnimatePresence initial={false}>
							{isTemporaryChat ? <motion.p key="temporary-subtitle" initial={{
            opacity: 0,
            scale: 0.97
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.97
          }} transition={{
            opacity: {
              duration: 0.06,
              ease: "easeOut"
            },
            scale: {
              duration: 0.12,
              ease: "easeOut"
            }
          }} className="mt-2 text-sm text-muted-foreground">
									{t("chatUi.temporaryChatSubtitle")}
								</motion.p> : null}
						</AnimatePresence>
					</motion.div>
					<AnimatePresence initial={false}>
						{isTemporaryChat ? null : <motion.div key="regular-chat-suggestions" initial={{
          opacity: 0,
          height: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          height: "auto",
          scale: 1
        }} exit={{
          opacity: 0,
          height: 0,
          scale: 0.97
        }} transition={{
          opacity: {
            duration: 0.06,
            ease: "easeOut"
          },
          height: {
            duration: 0.16,
            ease: "easeOut"
          },
          scale: {
            duration: 0.14,
            ease: "easeOut"
          }
        }} className="overflow-hidden">
								{visibleHeroSuggestionGroups.length > 1 ? <div className="mb-6 flex justify-center gap-2">
										{visibleHeroSuggestionGroups.map(key => <Button key={key} size="sm" type="button" variant={activeSuggestionGroup === key ? "default" : "secondary"} onClick={() => setActiveGroup(key)} className="rounded-full">
												{getSuggestionGroupLabel(key)}
											</Button>)}
									</div> : null}
								<AnimatePresence mode="wait">
									{randomizedHeroSuggestionGroups ? <motion.div key={activeSuggestionGroup} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.07,
              ease: "easeOut"
            }} className="space-y-2">
											{randomizedHeroSuggestionGroups[activeSuggestionGroup].map((s, index_0) => <motion.button key={s} type="button" initial={{
                opacity: 0,
                y: -6
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.12,
                delay: index_0 * 0.025,
                ease: "easeOut"
              }} onClick={() => {
                void handlePromptSubmit(s);
              }} className="w-full rounded-md border px-4 py-3 text-left text-sm hover:bg-muted/60">
													{s}
												</motion.button>)}
										</motion.div> : null}
								</AnimatePresence>
							</motion.div>}
					</AnimatePresence>
				</motion.div>
			</AnimatePresence> : <>
				{virtualItems.map(item => {
      const m = messages[item.index]!;
      const isLastMessage = item.index === messages.length - 1;
      return <div key={item.key} data-index={item.index} ref={virtualizer.measureElement} className="px-4" style={{
        position: "absolute",
        top: 0,
        left: 0,
        transform: `translateY(${item.start}px)`,
        width: "100%"
      }}>
							{m.role === "assistant" ? <AssistantMessage message={m} isLastMessage={isLastMessage} status={isLastMessage ? status : "ready"} regenerate={regenerate} finishReason={isLastMessage ? finishReason : null} forkChat={isLastMessage && status === "ready" ? forkChat : undefined} isForkingChat={isForkingChat} /> : <VirtualUserMessageItem message={m} isLastMessage={isLastMessage} status={status} canEdit={canEditUserMessages} editingMessageId={editingMessageId} setEditingMessageId={setEditingMessageId} onEditUserMessage={onEditUserMessage} />}
						</div>;
    })}
				{status === "submitted" && <div style={{
      position: "absolute",
      top: 0,
      left: 0,
      transform: `translateY(${virtualizer.getTotalSize()}px)`,
      width: "100%"
    }} className="px-4 mt-3">
						<Loader />
					</div>}
				{messages.length > 0 && messages[messages.length - 1].role === "user" && error && <div style={{
      position: "absolute",
      top: 0,
      left: 0,
      transform: `translateY(${virtualizer.getTotalSize()}px)`,
      width: "100%"
    }} className="px-4 mt-2 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
							<AlertTriangle className="size-3.5 shrink-0" />
							<span>{error}</span>
						</div>}
			</>;
  const inputArea = <div ref={floatingInput ? inputRef : undefined} className={floatingInput ? "absolute bottom-0 left-0 right-0 z-10 px-0 pb-0 sm:px-4 pointer-events-none select-none" : "shrink-0 px-4 pb-[max(env(safe-area-inset-bottom),1rem)] pt-2 bg-background border-t select-none"}>
			<motion.div layout className={floatingInput ? "mx-auto w-full max-w-4xl px-0 pb-0 pt-2 bg-background sm:px-4 pointer-events-auto" : undefined} transition={{
      duration: 0.18,
      ease: "easeOut"
    }}>
				<PromptInput key={`prompt-input-${supportsImages ? "img" : ""}${supportsAudio ? "aud" : ""}${supportsDocuments ? "doc" : ""}`} className={floatingInput ? "[&_[data-slot=input-group]]:rounded-none [&_[data-slot=input-group]]:border-x-0 [&_[data-slot=input-group]]:border-b-0 sm:[&_[data-slot=input-group]]:rounded-md sm:[&_[data-slot=input-group]]:border" : undefined} accept={supportsDocuments ? undefined : [supportsImages ? "image/*" : null, supportsAudio ? "audio/*" : null].filter(Boolean).join(",") || undefined} multiple globalDrop={supportsImages || supportsAudio || supportsDocuments} aria-disabled={isBusy} onSubmit={message => {
        void handlePromptSubmit(message.text ?? "", message.files);
      }}>
					<PromptInputBody>
						{activeSkills.length > 0 && <div className="w-full flex flex-wrap gap-1.5 px-2 pt-2">
								{activeSkills.map(skill_0 => <SkillChip key={skill_0.id} skill={skill_0} onRemove={() => onRemoveSkill?.(skill_0.id)} />)}
							</div>}
						<PromptInputAttachments>
							{attachment => <PromptInputAttachment data={attachment} />}
						</PromptInputAttachments>
						<PromptInputTextarea ref={textareaRef} value={text} onChange={e_0 => {
            const value_0 = e_0.currentTarget.value;
            const cursor = e_0.currentTarget.selectionStart ?? value_0.length;
            setText(value_0);
            const textUpToCursor = value_0.slice(0, cursor);
            const match = textUpToCursor.match(/(?:^|(?<=\s))@([a-zA-Z_-]*)$/);
            if (match) {
              const idx = cursor - match[0].length;
              skillTriggerIndexRef.current = idx;
              setSkillTriggerFilter(match[1].toLowerCase());
              setSkillTriggerOpen(true);
              if (textareaRef.current) {
                const coords = getCaretCoordinates(textareaRef.current, idx);
                const rect = textareaRef.current.getBoundingClientRect();
                setSkillTriggerPos({
                  top: rect.top + coords.top - textareaRef.current.scrollTop,
                  left: rect.left + coords.left
                });
              }
            } else {
              setSkillTriggerOpen(false);
              skillTriggerIndexRef.current = -1;
            }
          }} placeholder={t("chatUi.messagePlaceholder")} disabled={isLoading} />
					</PromptInputBody>
					<PromptInputToolbar>
						<PromptInputTools>
							{(supportsImages || supportsAudio || supportsDocuments) && <PromptInputActionMenu>
									<PromptInputActionMenuTrigger />
									<PromptInputActionMenuContent>
										<PromptInputActionAddAttachments label={(() => {
                  const parts_0: string[] = [];
                  if (supportsImages) {
                    parts_0.push(t("chatUi.photos"));
                  }
                  if (supportsAudio) {
                    parts_0.push(t("studio.audio"));
                  }
                  if (supportsDocuments) {
                    parts_0.push(t("chatUi.documents"));
                  }
                  if (parts_0.length === 0) {
                    return undefined;
                  }
                  if (parts_0.length === 1) {
                    return t("chatUi.addAttachments", {
                      items: parts_0[0]
                    });
                  }
                  const last = parts_0.pop();
                  return t("chatUi.addAttachmentsOr", {
                    items: parts_0.join(", "),
                    last: last ?? ""
                  });
                })()} />
									</PromptInputActionMenuContent>
								</PromptInputActionMenu>}
							<PromptInputSpeechButton onTranscriptionChange={setText} textareaRef={textareaRef} />
							{supportsWebSearch && <PromptInputButton variant={webSearchEnabled ? "default" : "ghost"} onClick={() => setWebSearchEnabled(!webSearchEnabled)}>
									<GlobeIcon size={16} />
								</PromptInputButton>}
							<SkillPickerButton onSelectSkill={onSelectSkill} activeSkills={activeSkills} />
						</PromptInputTools>
						<div className="flex items-center gap-2">
							{supportsReasoning && <Select value={reasoningEffort ? reasoningEffort : "off"} onValueChange={val => setReasoningEffort(val === "off" ? "" : val as "minimal" | "low" | "medium" | "high" ?? "")}>
									<SelectTrigger size="sm" className="min-w-[120px]">
										<Brain size={16} />
										<SelectValue placeholder={t("chatUi.reasoning")} />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="off">
											{getReasoningLabel("off")}
										</SelectItem>
										{selectedModel.includes("gpt-5") && <SelectItem value="minimal">
												{getReasoningLabel("minimal")}
											</SelectItem>}
										<SelectItem value="low">
											{getReasoningLabel("low")}
										</SelectItem>
										<SelectItem value="medium">
											{getReasoningLabel("medium")}
										</SelectItem>
										<SelectItem value="high">
											{getReasoningLabel("high")}
										</SelectItem>
									</SelectContent>
								</Select>}
							{supportsImageGen && !usesPixelDimensions && <>
									<Select value={imageAspectRatio} onValueChange={val_0 => setImageAspectRatio(val_0 as "auto" | "1:1" | "9:16" | "16:9" | "3:4" | "4:3" | "3:2" | "2:3" | "5:4" | "4:5" | "21:9" | "1:4" | "4:1" | "1:8" | "8:1")}>
										<SelectTrigger size="sm" className="min-w-[110px]">
											<SelectValue placeholder={t("studio.aspectRatio")} />
										</SelectTrigger>
										<SelectContent>
											{["auto", "1:1", "9:16", "16:9", "3:4", "4:3", "3:2", "2:3", "5:4", "4:5", "21:9", "1:4", "4:1", "1:8", "8:1"].map(r => <SelectItem key={r} value={r}>
													<span className="flex items-center gap-2">
														<AspectRatioIcon ratio={r} />
														{r === "auto" ? t("chatUi.auto") : r}
													</span>
												</SelectItem>)}
										</SelectContent>
									</Select>
									<Select value={imageSize} onValueChange={setImageSize}>
										<SelectTrigger size="sm" className="min-w-[80px]">
											<SelectValue placeholder={t("studio.resolution")} />
										</SelectTrigger>
										<SelectContent>
											{availableSizes.map(size => <SelectItem key={size} value={size}>
													{size}
												</SelectItem>)}
										</SelectContent>
									</Select>
								</>}
							{supportsImageGen && usesPixelDimensions && isGptImage && <>
									<Select value={alibabaImageSize} onValueChange={setAlibabaImageSize}>
										<SelectTrigger size="sm" className="min-w-[130px]">
											<SelectValue placeholder={t("studio.resolution")} />
										</SelectTrigger>
										<SelectContent>
											{GPT_IMAGE_SIZES.map(size_0 => <SelectItem key={size_0} value={size_0}>
													{size_0 === "auto" ? t("chatUi.auto") : size_0}
												</SelectItem>)}
										</SelectContent>
									</Select>
									<Select value={imageQuality} onValueChange={setImageQuality}>
										<SelectTrigger size="sm" className="min-w-[100px]">
											<SelectValue placeholder={t("studio.quality")} />
										</SelectTrigger>
										<SelectContent>
											{qualityOptions.map(q => <SelectItem key={q} value={q}>
													{q === "auto" ? t("chatUi.auto") : getReasoningLabel(q)}
												</SelectItem>)}
										</SelectContent>
									</Select>
								</>}
							{supportsImageGen && usesPixelDimensions && !isGptImage && <Select value={alibabaImageSize} onValueChange={setAlibabaImageSize}>
									<SelectTrigger size="sm" className="min-w-[130px]">
										<SelectValue placeholder={t("studio.imageSize")} />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="1024x1024">1024x1024</SelectItem>
										<SelectItem value="720x1280">720x1280</SelectItem>
										<SelectItem value="1280x720">1280x720</SelectItem>
										<SelectItem value="1024x1536">1024x1536</SelectItem>
										<SelectItem value="1536x1024">1536x1024</SelectItem>
										<SelectItem value="2048x1024">2048x1024</SelectItem>
										<SelectItem value="1024x2048">1024x2048</SelectItem>
									</SelectContent>
								</Select>}
							{supportsImageGen && <Select value={String(imageCount)} onValueChange={val_1 => setImageCount(Number(val_1) as 1 | 2 | 3 | 4)}>
									<SelectTrigger size="sm" className="min-w-[90px]">
										<SelectValue placeholder={t("studio.count")} />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="1">
											{t("studio.imageCount", {
                    count: 1
                  })}
										</SelectItem>
										<SelectItem value="2">
											{t("studio.imageCount", {
                    count: 2
                  })}
										</SelectItem>
										<SelectItem value="3">
											{t("studio.imageCount", {
                    count: 3
                  })}
										</SelectItem>
										<SelectItem value="4">
											{t("studio.imageCount", {
                    count: 4
                  })}
										</SelectItem>
									</SelectContent>
								</Select>}
							<PromptInputSubmit status={status === "streaming" ? "streaming" : status === "submitted" ? "submitted" : "ready"} disabled={isLoading} />
						</div>
					</PromptInputToolbar>
				</PromptInput>
				<SkillTriggerMenu open={skillTriggerOpen} position={skillTriggerPos} filter={skillTriggerFilter} onSelect={handleSkillTriggerSelect} onClose={() => setSkillTriggerOpen(false)} />
				<AnimatePresence initial={false}>
					{isTemporaryChat ? <motion.p key="temporary-chat-disclosure" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 8
        }} transition={{
          duration: 0.16,
          ease: "easeOut"
        }} className="px-1 pt-2 pb-3 text-center text-xs text-muted-foreground">
							Some responses are saved for up to 72 hours before they are
							deleted, read our{" "}
							<a href={getMarketingUrl("/legal/terms")} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 transition-colors hover:text-foreground">
								terms of use
							</a>{" "}
							for more details
						</motion.p> : null}
				</AnimatePresence>
			</motion.div>
		</div>;
  const virtualScrollContextValue = useMemo(() => ({
    isAtEnd,
    scrollToEnd
  }), [isAtEnd, scrollToEnd]);
  if (floatingInput) {
    return <VirtualScrollContext value={virtualScrollContextValue}>
				<div className="relative flex flex-col h-full min-h-0">
					<div ref={scrollRef} className="flex-1 overflow-y-auto" onScroll={handleScroll} role="log" style={messages.length > 0 ? {
          paddingBottom: `${inputHeight + 16}px`
        } : undefined}>
						<div className={`mx-auto max-w-4xl relative ${messages.length === 0 ? "flex min-h-full w-full items-center justify-center px-4" : ""}`} style={messages.length > 0 ? {
            height: virtualizer.getTotalSize() + extraScrollHeight
          } : {
            minHeight: "100%"
          }}>
							{messagesContent}
						</div>
					</div>
					<ConversationScrollButton />
					{inputArea}
				</div>
			</VirtualScrollContext>;
  }
  return <VirtualScrollContext value={virtualScrollContextValue}>
			<div className="relative flex flex-col h-full min-h-0">
				<div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0" onScroll={handleScroll} role="log">
					<div className={`mx-auto max-w-4xl relative ${messages.length === 0 ? "flex min-h-full items-center justify-center px-4" : ""}`} style={messages.length > 0 ? {
          height: virtualizer.getTotalSize() + extraScrollHeight
        } : {
          minHeight: "100%"
        }}>
						{messagesContent}
					</div>
				</div>
				<ConversationScrollButton />
				{inputArea}
			</div>
		</VirtualScrollContext>;
};
function SkillChip(t0) {
  const $ = _c(9);
  const {
    skill,
    onRemove
  } = t0;
  const {
    t
  } = useI18n();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ScrollTextIcon className="h-3 w-3 text-muted-foreground" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== skill.name) {
    t2 = <span>{skill.name}</span>;
    $[1] = skill.name;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onRemove || $[4] !== t) {
    t3 = onRemove && <button type="button" onClick={onRemove} className="text-muted-foreground hover:text-foreground ml-0.5 rounded" aria-label={t("chatUi.removeSkill")}><XIcon className="h-3 w-3" /></button>;
    $[3] = onRemove;
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="inline-flex items-center gap-1.5 rounded-md border bg-muted px-2 py-1 text-xs font-medium">{t1}{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function SkillPickerButton(t0) {
  const $ = _c(46);
  const {
    onSelectSkill,
    activeSkills: t1
  } = t0;
  const activeSkills = t1 === undefined ? [] : t1;
  const {
    t
  } = useI18n();
  const [createOpen, setCreateOpen] = useState(false);
  const router = useRouter();
  const {
    data
  } = useSkills();
  const skills = data?.skills as Skill[] | undefined ?? [];
  const enabledSkills = skills.filter(_temp3);
  const activeIds = new Set(activeSkills.map(_temp4));
  const T0 = Popover;
  const t2 = activeSkills.length > 0 ? "default" : "ghost";
  let t3;
  if ($[0] !== t) {
    t3 = t("chatUi.selectSkill");
    $[0] = t;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ScrollTextIcon size={16} />;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t2 || $[4] !== t3) {
    t5 = <PopoverTrigger asChild={true}><PromptInputButton variant={t2} aria-label={t3}>{t4}</PromptInputButton></PopoverTrigger>;
    $[3] = t2;
    $[4] = t3;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const T1 = PopoverContent;
  const t6 = "start";
  const t7 = "w-64 p-1";
  let t8;
  if ($[6] !== t) {
    t8 = t("chatUi.selectSkill");
    $[6] = t;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t8) {
    t9 = <span className="text-xs font-medium text-muted-foreground">{t8}</span>;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t) {
    t10 = t("chatUi.viewAllSkills");
    $[10] = t;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== router) {
    t11 = () => router.push("/skills");
    $[12] = router;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ExternalLinkIcon className="h-3.5 w-3.5" />;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t10 || $[16] !== t11) {
    t13 = <button type="button" className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label={t10} onClick={t11}>{t12}</button>;
    $[15] = t10;
    $[16] = t11;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t) {
    t14 = t("chatUi.createSkill");
    $[18] = t;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== setCreateOpen) {
    t15 = () => setCreateOpen(true);
    $[20] = setCreateOpen;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <PlusIcon className="h-3.5 w-3.5" />;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t14 || $[24] !== t15) {
    t17 = <button type="button" className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label={t14} onClick={t15}>{t16}</button>;
    $[23] = t14;
    $[24] = t15;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t13 || $[27] !== t17) {
    t18 = <div className="flex items-center gap-0.5">{t13}{t17}</div>;
    $[26] = t13;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t18 || $[30] !== t9) {
    t19 = <div className="mb-1 flex items-center justify-between px-2 py-1">{t9}{t18}</div>;
    $[29] = t18;
    $[30] = t9;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  const t20 = enabledSkills.length === 0 ? <p className="px-2 py-3 text-center text-xs text-muted-foreground">{t("chatUi.noSkillsYetCreate")}</p> : enabledSkills.map(skill => {
    const isActive = activeIds.has(skill.id);
    return <button key={skill.id} type="button" className="flex w-full items-start gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted" onClick={() => !isActive && onSelectSkill?.(skill)}><ScrollTextIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" /><div className="min-w-0 flex-1"><div className="truncate font-medium">{skill.name}</div>{skill.description && <div className="truncate text-xs text-muted-foreground">{skill.description}</div>}</div>{isActive && <span className="ml-auto text-xs text-muted-foreground">✓</span>}</button>;
  });
  let t21;
  if ($[32] !== T1 || $[33] !== t19 || $[34] !== t20) {
    t21 = <T1 align={t6} className={t7}>{t19}{t20}</T1>;
    $[32] = T1;
    $[33] = t19;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== T0 || $[37] !== t21 || $[38] !== t5) {
    t22 = <T0>{t5}{t21}</T0>;
    $[36] = T0;
    $[37] = t21;
    $[38] = t5;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== createOpen || $[41] !== setCreateOpen) {
    t23 = <CreateSkillDialog open={createOpen} onOpenChange={setCreateOpen} />;
    $[40] = createOpen;
    $[41] = setCreateOpen;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== t22 || $[44] !== t23) {
    t24 = <>{t22}{t23}</>;
    $[43] = t22;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  return t24;
}
function _temp4(s_0) {
  return s_0.id;
}
function _temp3(s) {
  return s.enabled;
}
function SkillTriggerMenu(t0) {
  const $ = _c(28);
  const {
    open,
    position,
    filter,
    onSelect,
    onClose
  } = t0;
  const {
    data
  } = useSkills();
  const [highlight, setHighlight] = useState(0);
  let t1;
  if ($[0] !== data?.skills || $[1] !== filter) {
    const skills = data?.skills as Skill[] | undefined ?? [];
    let t2;
    if ($[3] !== filter) {
      t2 = s_0 => !filter || s_0.name.toLowerCase().includes(filter);
      $[3] = filter;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = skills.filter(_temp5).filter(t2);
    $[0] = data?.skills;
    $[1] = filter;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const filtered = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setHighlight(0);
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== filter) {
    t3 = [filter];
    $[6] = filter;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[8] !== filtered || $[9] !== highlight || $[10] !== onClose || $[11] !== onSelect || $[12] !== open) {
    t4 = () => {
      if (!open) {
        return;
      }
      const handler = e => {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setHighlight(h => (h + 1) % Math.max(filtered.length, 1));
        } else {
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight(h_0 => (h_0 - 1 + Math.max(filtered.length, 1)) % Math.max(filtered.length, 1));
          } else {
            if (e.key === "Enter") {
              e.preventDefault();
              if (filtered[highlight]) {
                onSelect(filtered[highlight]);
              }
            } else {
              if (e.key === "Escape") {
                onClose();
              }
            }
          }
        }
      };
      window.addEventListener("keydown", handler, true);
      return () => window.removeEventListener("keydown", handler, true);
    };
    t5 = [open, filtered, highlight, onSelect, onClose];
    $[8] = filtered;
    $[9] = highlight;
    $[10] = onClose;
    $[11] = onSelect;
    $[12] = open;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useEffect(t4, t5);
  if (!open || filtered.length === 0) {
    return null;
  }
  const itemsHeight = filtered.length * 52;
  const menuHeight = Math.min(itemsHeight + 8, 240);
  const t6 = position.top - menuHeight - 6;
  let t7;
  if ($[15] !== position.left || $[16] !== t6) {
    t7 = {
      position: "fixed",
      top: t6,
      left: position.left,
      zIndex: 9999,
      width: 240
    };
    $[15] = position.left;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== filtered || $[19] !== highlight || $[20] !== onSelect) {
    let t9;
    if ($[22] !== highlight || $[23] !== onSelect) {
      t9 = (skill, i) => <button key={skill.id} type="button" className={cn("flex w-full items-start gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors", i === highlight ? "bg-muted" : "hover:bg-muted")} onMouseEnter={() => setHighlight(i)} onClick={() => onSelect(skill)}><ScrollTextIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" /><div className="min-w-0"><div className="truncate font-medium">{skill.name}</div>{skill.description && <div className="truncate text-xs text-muted-foreground">{skill.description}</div>}</div></button>;
      $[22] = highlight;
      $[23] = onSelect;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    t8 = filtered.map(t9);
    $[18] = filtered;
    $[19] = highlight;
    $[20] = onSelect;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[25] !== t7 || $[26] !== t8) {
    t9 = createPortal(<div style={t7} className="rounded-md border bg-popover p-1 shadow-md">{t8}</div>, document.body);
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  return t9;
}
function _temp5(s) {
  return s.enabled;
}
function _temp2(tool) {
  return <Tool key={tool.toolCallId}><ToolHeader title={tool.toolName} type={tool.type as `tool-${string}`} state={tool.state} /><ToolContent><ToolInput input={tool.input} /><ToolOutput errorText={tool.errorText} output={tool.output} /></ToolContent></Tool>;
}